// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminOrgScope } from '@/hooks/useAdminOrgScope';
import { Loader2, ClipboardList, Download, FileText, ArrowUpDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ScoreSheetImageLink from '@/components/tournament/ScoreSheetImageLink';

interface RecordRow {
  gameId: string;
  eventId: string;
  eventName: string;
  date: string | null;
  division: string;
  teamAName: string;
  teamBName: string;
  scoreA: number | null;
  scoreB: number | null;
  winnerName: string;
  aWin: boolean;
  bWin: boolean;
  scoreSheetUrl: string | null;
}

const fmtDate = (d: string | null) => {
  if (!d) return '';
  const [y, m, day] = String(d).split('-').map(Number);
  if (!y) return d;
  return new Date(y, (m || 1) - 1, day || 1).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

type SortKey = 'event' | 'date' | 'division';

const RecordsManagement: React.FC = () => {
  const { scopeOrgId, ready } = useAdminOrgScope();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<RecordRow[]>([]);

  // Filters
  const [eventFilter, setEventFilter] = useState('all');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [teamQuery, setTeamQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Sort
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (!ready) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, scopeOrgId]);

  const load = async () => {
    setLoading(true);
    try {
      // 1. Org events
      let evQuery = supabase
        .from('events')
        .select('id, name, published_schedule_version_id')
        .is('deleted_at', null);
      if (scopeOrgId) evQuery = evQuery.eq('organization_id', scopeOrgId);
      const { data: events } = await evQuery;
      const eventList = events || [];
      if (!eventList.length) {
        setRows([]);
        setLoading(false);
        return;
      }
      const eventNameById = new Map<string, string>();
      eventList.forEach((e) => eventNameById.set(e.id, e.name || 'Event'));

      // 2. Resolve schedule version per event (published, else latest).
      const eventIds = eventList.map((e) => e.id);
      const versionByEvent = new Map<string, string>();
      const needLatest: string[] = [];
      eventList.forEach((e) => {
        if (e.published_schedule_version_id) {
          versionByEvent.set(e.id, e.published_schedule_version_id);
        } else {
          needLatest.push(e.id);
        }
      });
      if (needLatest.length) {
        const { data: vers } = await supabase
          .from('schedule_versions')
          .select('id, event_id, created_at')
          .in('event_id', needLatest)
          .order('created_at', { ascending: false });
        (vers || []).forEach((v: any) => {
          if (!versionByEvent.has(v.event_id)) versionByEvent.set(v.event_id, v.id);
        });
      }
      const versionIds = [...versionByEvent.values()];
      const eventByVersion = new Map<string, string>();
      versionByEvent.forEach((vid, eid) => eventByVersion.set(vid, eid));
      if (!versionIds.length) {
        setRows([]);
        setLoading(false);
        return;
      }

      // 3. Completed games across all those versions.
      const { data: games } = await supabase
        .from('scheduled_games')
        .select(
          'id, schedule_version_id, division, scheduled_date, game_status, score_a, score_b, score_sheet_image_url, winner_registration_id, team_a_registration_id, team_b_registration_id',
        )
        .in('schedule_version_id', versionIds)
        .eq('game_status', 'completed');
      const gameList = games || [];

      // 4. Team names come from the registration behind each game.
      const nameByRegId = await fetchRegistrationTeamNames(
        gameList.flatMap((g: any) => [g.team_a_registration_id, g.team_b_registration_id]),
      );
      const nameFor = (regId: string | null) => (regId && nameByRegId[regId]) || 'TBD';

      const built: RecordRow[] = gameList.map((g: any) => {
        const a = g.score_a;
        const b = g.score_b;
        const aWin = g.winner_registration_id
          ? g.winner_registration_id === g.team_a_registration_id
          : a != null && b != null && a > b;
        const bWin = g.winner_registration_id
          ? g.winner_registration_id === g.team_b_registration_id
          : a != null && b != null && b > a;
        const teamAName = nameFor(g.team_a_registration_id);
        const teamBName = nameFor(g.team_b_registration_id);

        const eid = eventByVersion.get(g.schedule_version_id) || '';
        return {
          gameId: g.id,
          eventId: eid,
          eventName: eventNameById.get(eid) || 'Event',
          date: g.scheduled_date,
          division: g.division && g.division !== '(unspecified)' ? g.division : '',
          teamAName,
          teamBName,
          scoreA: a,
          scoreB: b,
          winnerName: aWin ? teamAName : bWin ? teamBName : '',
          aWin,
          bWin,
          scoreSheetUrl: g.score_sheet_image_url || null,
        };
      });
      setRows(built);
    } finally {
      setLoading(false);
    }
  };

  const eventOptions = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => m.set(r.eventId, r.eventName));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const divisionOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.division && s.add(r.division));
    return [...s].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = teamQuery.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (eventFilter !== 'all' && r.eventId !== eventFilter) return false;
      if (divisionFilter !== 'all' && r.division !== divisionFilter) return false;
      if (q && !(`${r.teamAName} ${r.teamBName}`.toLowerCase().includes(q))) return false;
      if (fromDate && (!r.date || r.date < fromDate)) return false;
      if (toDate && (!r.date || r.date > toDate)) return false;
      return true;
    });
    out = [...out].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'event') cmp = a.eventName.localeCompare(b.eventName);
      else if (sortKey === 'division') cmp = a.division.localeCompare(b.division);
      else cmp = String(a.date || '').localeCompare(String(b.date || ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, eventFilter, divisionFilter, teamQuery, fromDate, toDate, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'date' ? 'desc' : 'asc');
    }
  };

  const exportCsv = () => {
    const esc = (v: any) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      'event',
      'date',
      'division',
      'team_a',
      'score_a',
      'team_b',
      'score_b',
      'winner',
      'score_sheet_url',
    ];
    const lines = filtered.map((r) =>
      [
        r.eventName,
        r.date || '',
        r.division,
        r.teamAName,
        r.scoreA ?? '',
        r.teamBName,
        r.scoreB ?? '',
        r.winnerName,
        r.scoreSheetUrl || '',
      ]
        .map(esc)
        .join(','),
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `records-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <ClipboardList className="h-6 w-6" />
            Records
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Final scores and score sheets across your events.
          </p>
        </div>
        <Button onClick={exportCsv} variant="outline" disabled={!filtered.length}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Select value={eventFilter} onValueChange={setEventFilter}>
          <SelectTrigger>
            <SelectValue placeholder="Event" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            {eventOptions.map(([id, name]) => (
              <SelectItem key={id} value={id}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={divisionFilter} onValueChange={setDivisionFilter}>
          <SelectTrigger>
            <SelectValue placeholder="Division" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All divisions</SelectItem>
            {divisionOptions.map((d) => (
              <SelectItem key={d} value={d}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Search team…"
          value={teamQuery}
          onChange={(e) => setTeamQuery(e.target.value)}
        />
        <Input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          aria-label="From date"
        />
        <Input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          aria-label="To date"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading records…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
          No completed games yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => toggleSort('event')}
                    className="inline-flex items-center gap-1 font-semibold hover:underline"
                  >
                    Event <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => toggleSort('date')}
                    className="inline-flex items-center gap-1 font-semibold hover:underline"
                  >
                    Date <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => toggleSort('division')}
                    className="inline-flex items-center gap-1 font-semibold hover:underline"
                  >
                    Division <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="px-3 py-2">Matchup &amp; Score</th>
                <th className="px-3 py-2">Score Sheet</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((r) => (
                <tr key={r.gameId} className="align-top">
                  <td className="px-3 py-2">{r.eventName}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                    {fmtDate(r.date)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                    {r.division || '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={r.aWin ? 'font-bold' : ''}>{r.teamAName}</span>
                    <span className="mx-1 tabular-nums">
                      {r.scoreA ?? 0} – {r.scoreB ?? 0}
                    </span>
                    <span className={r.bWin ? 'font-bold' : ''}>{r.teamBName}</span>
                  </td>
                  <td className="px-3 py-2">
                    {r.scoreSheetUrl ? (
                      <ScoreSheetImageLink url={r.scoreSheetUrl} />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RecordsManagement;
